import { create } from 'zustand';
import type { AdminUserProfile } from '../lib/admin-auth-api';

export const ADMIN_SUPPRESS_BOOTSTRAP_STORAGE_KEY =
  'admin_auth_suppress_bootstrap_until_login';

const SUPPRESS_BOOTSTRAP_STORAGE_MARKER = '1';

interface AdminAuthStoreState {
  accessToken: string | null;
  user: AdminUserProfile | null;
  isBootstrapping: boolean;
  suppressBootstrapUntilLogin: boolean;
  setSession: (accessToken: string, user: AdminUserProfile) => void;
  clearSession: () => void;
  setBootstrapping: (isBootstrapping: boolean) => void;
  setSuppressBootstrap: (suppressBootstrapUntilLogin: boolean) => void;
}

function readSuppressBootstrapFromStorage(): boolean {
  try {
    return localStorage.getItem(ADMIN_SUPPRESS_BOOTSTRAP_STORAGE_KEY) === SUPPRESS_BOOTSTRAP_STORAGE_MARKER;
  } catch {
    return false;
  }
}

function writeSuppressBootstrapToStorage(suppressBootstrapUntilLogin: boolean): void {
  try {
    if (suppressBootstrapUntilLogin) {
      localStorage.setItem(
        ADMIN_SUPPRESS_BOOTSTRAP_STORAGE_KEY,
        SUPPRESS_BOOTSTRAP_STORAGE_MARKER,
      );
      return;
    }

    localStorage.removeItem(ADMIN_SUPPRESS_BOOTSTRAP_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in private mode/sandboxed contexts.
  }
}

export function createInitialAdminAuthState(): AdminAuthStoreState {
  return {
    accessToken: null,
    user: null,
    isBootstrapping: false,
    suppressBootstrapUntilLogin: readSuppressBootstrapFromStorage(),
    setSession: (accessToken, user) => {
      writeSuppressBootstrapToStorage(false);
      useAdminAuthStore.setState({
        accessToken,
        user,
        suppressBootstrapUntilLogin: false,
      });
    },
    clearSession: () =>
      useAdminAuthStore.setState({
        accessToken: null,
        user: null,
      }),
    setBootstrapping: (isBootstrapping) =>
      useAdminAuthStore.setState({
        isBootstrapping,
      }),
    setSuppressBootstrap: (suppressBootstrapUntilLogin) => {
      writeSuppressBootstrapToStorage(suppressBootstrapUntilLogin);
      useAdminAuthStore.setState((state) => ({
        suppressBootstrapUntilLogin,
        isBootstrapping: suppressBootstrapUntilLogin ? false : state.isBootstrapping,
      }));
    },
  };
}

export const useAdminAuthStore = create<AdminAuthStoreState>(() => createInitialAdminAuthState());
